/**
 * @component NewsDetailPage
 * @description 선택된 뉴스의 본문과 발행 정보를 상세 노출하는 콘텐츠 상세 페이지입니다.
 * @usage App 라우팅에서 `/news/:slug.html` 경로일 때 렌더링됩니다.
 */

import { useState, useEffect } from 'react';
import PageHero from '../../components/common/PageHero';

// [기능] 기사 본문을 형식에 맞게 렌더링합니다.
// [비즈니스] 본문은 두 가지 형식으로 존재할 수 있습니다.
//            리치 에디터 도입 이전 기사는 문단 문자열 배열, 이후 기사는 HTML 문자열입니다.
//            과거 기사를 계속 정상 노출하기 위해 두 형식을 모두 처리합니다.
// [기술] HTML 문자열은 저장 시점에 서버가 허용 태그만 남기도록 정화합니다.
//        작성 경로가 관리자 로그인으로 막혀 있고 저장 시 정화되므로 여기서 다시 정화하지 않습니다.
function NewsBody({ body }) {
  if (Array.isArray(body)) {
    return (
      <div className="news-detail-body">
        {body.map((paragraph, index) => (
          <p key={index}>{paragraph}</p>
        ))}
      </div>
    );
  }

  if (typeof body === 'string' && body) {
    return <div className="news-detail-body" dangerouslySetInnerHTML={{ __html: body }} />;
  }

  return null;
}

export default function NewsDetailPage({ slug, onNavigate }) {
  const [article, setArticle] = useState(null);
  const [loading, setLoading] = useState(true);

  // [기능] 백엔드 API에서 뉴스 목록을 가져와 slug에 해당하는 기사를 찾습니다.
  useEffect(() => {
    let isActive = true;
    fetch('/api/news')
      .then((r) => r.json())
      .then((items) => {
        // [파생 상태] URL slug와 일치하는 기사를 찾고, 없으면 첫 기사를 기본값으로 사용합니다.
        const found = items.find((item) => item.slug === slug) ?? items[0] ?? null;
        if (isActive) setArticle(found);
      })
      .catch(() => {
        // [비즈니스] 조회 실패 시 아래의 "기사를 찾을 수 없습니다" 화면으로 안내합니다.
      })
      .finally(() => {
        if (isActive) setLoading(false);
      });
    return () => {
      isActive = false;
    };
  }, [slug]);

  if (loading) {
    return (
      <section className="section">
        <div className="container">
          <div className="news-detail card-panel fade-in">
            <p>로딩 중...</p>
          </div>
        </div>
      </section>
    );
  }

  if (!article) {
    return (
      <>
        <PageHero title="News" trail={['News']} className="news-page-hero" />
        <section className="section">
          <div className="container">
            <article className="news-detail card-panel fade-in">
              <p>기사를 찾을 수 없습니다.</p>
              <button
                type="button"
                className="secondary-button"
                onClick={() => onNavigate('/news/index.html')}
              >
                목록으로
              </button>
            </article>
          </div>
        </section>
      </>
    );
  }

  return (
    <>
      <PageHero title={article.title} trail={['News', article.title]} className="news-page-hero" />
      <section className="section">
        <div className="container">
          <article className="news-detail card-panel fade-in">
            {/* [기술] 분류 배지 스타일은 이 클래스로만 적용합니다. 예전처럼 `.news-detail span`을
                      대상으로 하면 본문 안의 서식용 span까지 배지처럼 보이게 됩니다. */}
            <span className="news-detail-category">{article.category}</span>
            <h2>{article.title}</h2>
            <small>{article.date}</small>
            <NewsBody body={article.body} />
            {/* [이벤트 처리]: 뉴스 목록 페이지로 돌아갑니다. */}
            <button
              type="button"
              className="secondary-button"
              onClick={() => onNavigate('/news/index.html')}
            >
              목록으로
            </button>
          </article>
        </div>
      </section>
    </>
  );
}
